import { useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
// Utils
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { isNil, sortBy } from 'lodash';
// Components
import {
  Button, FilterBlock, DropdownList, DubbleSideButton,
} from '@repo/zolintra-ui';
// Hooks
import { useTranslation } from 'react-i18next';
import usePost from '../../../hooks/domain/usePost';
import useSearchParams from '../../../hooks/useSearchParams';
import useMonthSelector from '../../../hooks/useMonthSelector';
import useListSubView from './hook/useListSubView';
// Templates
import RemoteWorkingTable from './tables/RemoteWorkingTable';
// Services
import { usePostsListQuery } from '../../../services/post/postQueries';
// Constants
import { VIEW_DOMAIN } from '../../../constants';
// Styles
import styles from './ListSubView.module.css';
// Context
import { useAppContext } from '../../../store/AppContext';

const dateFnsLocales = {
  fr,
};

/**
 * @component
 * @returns {JSX.Element} The rendered ListSubViewRemoteWorking component.
 */
function ListSubViewRemoteWorking() {
  // Hooks
  const [{ user }] = useAppContext();
  const { getSearchParams } = useSearchParams();
  const { t } = useTranslation();
  const {
    getPostStatusList,
    getSlotsIdFromMonth,
  } = usePost();
  const { handleMonthSelectorClick, setMonthFilterOnToday } = useMonthSelector();

  // States
  const [isFilterDisplayed, setIsFilterDisplayed] = useState(false);
  const {
    year = new Date().getFullYear().toString(),
    month = new Date().getMonth().toString(),
    status,
    type: leaveType,
    resourceId,
  } = getSearchParams();
  const viewMonthSlotsId = getSlotsIdFromMonth({ month: Number(month), year });

  // Constants
  const statusOptions = getPostStatusList().map((option) => ({
    value: option.value,
    label: t(option.key),
  }));

  // API Calls
  const postListQuery = usePostsListQuery(
    { year },
    {
      includedStartSlotId: viewMonthSlotsId.startSlotId,
      excludedEndSlotId: viewMonthSlotsId.endSlotId + 1,
      status,
      type: VIEW_DOMAIN.REMOTE_WORKING,
      resourceId: user.id,
      businessUnit: null,
    },
    {
      refetchOnWindowFocus: true,
      enabled: !!user.id,
      placeholderData: (prev) => prev,
    },
  );

  const {
    acceptPostMutation,
    refusePostMutation,
    handleFilterChange,
    handleResetFilterClick,
  } = useListSubView(postListQuery);

  const availableFilters = [status, leaveType, resourceId];
  const activeFiltersCount = useMemo(() => availableFilters.reduce((acc, filter) => (
    acc + (!isNil(filter) ? 1 : 0)
  ), 0), [availableFilters]);

  return (
    <>
      <div className={styles['view-config']}>
        <div className={styles['view-switches']}>
          <DubbleSideButton
            onLeftClick={() => handleMonthSelectorClick('previous')}
            onRightClick={() => handleMonthSelectorClick('next')}
            label={format(new Date(+year, Number(month)), 'MMMM yyyy', { locale: dateFnsLocales.fr })}
            className="margin-left-m"
          />

          <Button
            onClick={setMonthFilterOnToday}
            color="transparent"
            className="margin-left-m margin-right-m"
          >
            <span className="font-bold">
              {t('remoteWorkingAndLeave.today')}
            </span>
          </Button>
        </div>

        <Button
          onClick={() => setIsFilterDisplayed(true)}
          color="transparent"
          className={activeFiltersCount > 0 ? styles['has-filters'] : ''}
          shape="rectangle"
        >
          <span className="font-bold">
            {t('remoteWorkingAndLeave.filtersBtnLabel')}
          </span>

          {activeFiltersCount > 0 ? (
            <span className={styles['has-filters-count']}>{activeFiltersCount}</span>
          ) : null}
        </Button>
      </div>

      <div className={styles.content}>
        <ErrorBoundary fallback={<>{t('common.wrong')}</>}>
          <RemoteWorkingTable
            data={sortBy(postListQuery.data?.data || [], 'includedStartSlotId')}
            isPostListFetching={postListQuery.isFetching}
            asyncActions={{
              acceptPost: (params) => acceptPostMutation.mutate(params),
              refusePost: (params) => refusePostMutation.mutate(params),
            }}
          />
        </ErrorBoundary>
      </div>

      <FilterBlock
        onClose={() => setIsFilterDisplayed(false)}
        closeLabel="Masquer les filtres"
        isOpen={isFilterDisplayed}
      >
        <div className={styles['content-filters']}>
          <DropdownList
            label={t('remoteWorkingAndLeave.listSubView.filters.status.label')}
            placeholder={t('remoteWorkingAndLeave.listSubView.filters.status.placeholder')}
            options={statusOptions}
            noOptionsMessage={t('common.noOptionsMessage')}
            value={statusOptions.find((option) => option.value === status) || null}
            onChange={(option) => handleFilterChange('status', option ? option.value : null)}
            isInsideDialog
            isClearable
          />

          <div className={styles.reset}>
            <Button
              onClick={handleResetFilterClick}
              color="transparent"
            >
              <span className="font-bold">
                {t('remoteWorkingAndLeave.listSubView.filters.resetFilterLabel')}
              </span>
            </Button>
          </div>
        </div>
      </FilterBlock>
    </>
  );
}

ListSubViewRemoteWorking.propTypes = {};

export default ListSubViewRemoteWorking;
